import React, { useContext } from 'react';
import { Text } from '@oneaudi/unified-web-components';
import { UeElement, UeReference } from '@oneaudi/falcon-tools';
import { useTheme } from 'styled-components';
import { Tile } from './TileComponent';
import type { Theme } from '../../types/theme';
import {
  ListItem,
  LinkCard,
  IconContainer,
  LinkIconContainer,
  AlphaTileGlobalStyles,
} from './styles';
import { ExternalLink, Forward } from '../Icon';
import { CardIcon } from './CardIcon';
import type { CardContentProps } from '../../types/shared';
import { useTrackingService } from '../../context/TrackingServiceContext';
import { getClickEvent, getClickEventAction, getSanitizedUrl } from '../../../tracking/clickEvent';
import { ContentContext } from '../../context/ContentContext';

export const SecondaryLinkCard: React.FC<CardContentProps> = ({ cardData, index }) => {
  const { trackingService } = useTrackingService();

  const context = useContext(ContentContext);
  const { loggerService } = context;
  const theme = useTheme();
  const onCardClick = (e: React.MouseEvent<HTMLElement>) => {
    if (trackingService) {
      const sanitizedUrl = getSanitizedUrl(trackingService, cardData);
      const eventAction = getClickEventAction(trackingService, sanitizedUrl);

      const event = getClickEvent(eventAction, sanitizedUrl, cardData, index);

      trackingService.track({ event });
      if (loggerService?.info && typeof loggerService.info === 'function') {
        loggerService.info(trackingService.featureAppId, event);
      }
      e.stopPropagation();
    }
  };

  const { showIcon, openLinkNewTab, URL, cardTitle, iconName } = cardData;

  let title = cardTitle;
  if (title.length > 44) {
    title = title.slice(0, 44);
  }

  const isOpenLinkInNewTab = openLinkNewTab === 'New Tab';

  let iconStrokeColor: string;
  if (process.env.IS_LEGACY) {
    iconStrokeColor = (theme as Theme).name === 'dark' ? '#666' : '#000';
  } else {
    iconStrokeColor = '#fff';
  }

  return (
    <>
      {!process.env.IS_LEGACY && <AlphaTileGlobalStyles showIcon={showIcon} />}
      <UeReference propertyPath={`cards.${index}`} behavior="component">
        {(ueRefProps) => (
          <ListItem {...ueRefProps}>
            {process.env.IS_LEGACY ? (
              <LinkCard
                aria-label={cardData.alt || title}
                data-testid="secondary-link-card"
                showIcon={showIcon}
                href={URL}
                target={isOpenLinkInNewTab ? '_blank' : '_self'}
                rel="noopener noreferrer"
                onClick={onCardClick}
              >
                <IconContainer showIcon={showIcon} data-testid="icon-container">
                  <CardIcon stroke={iconStrokeColor} iconName={iconName} testid="card-icon" />
                </IconContainer>
                <Text tag="p" variant={{ order: '1', style: 'normal' }} {...ueRefProps}>
                  <UeElement type="text" property="cardTitle" label="Card Title">
                    {title}
                  </UeElement>
                </Text>
                <LinkIconContainer>
                  {isOpenLinkInNewTab ? (
                    <ExternalLink stroke={iconStrokeColor} data-testid="link-icon" />
                  ) : (
                    <Forward stroke={iconStrokeColor} data-testid="link-icon" />
                  )}
                </LinkIconContainer>
              </LinkCard>
            ) : (
              <Tile
                href={URL}
                target={isOpenLinkInNewTab ? '_blank' : '_self'}
                rel="noopener noreferrer"
                onClick={onCardClick}
                aria-label={cardData.alt || title}
                data-testid="secondary-link-card"
                className="alpha-tile-spacing"
              >
                <IconContainer showIcon={showIcon} data-testid="icon-container">
                  <CardIcon stroke={iconStrokeColor} iconName={iconName} testid="card-icon" />
                </IconContainer>
                <Text tag="p" variant={{ order: '2', style: 'normal' }} {...ueRefProps}>
                  <UeElement type="text" property="cardTitle" label="Card Title">
                    {title}
                  </UeElement>
                </Text>
                <LinkIconContainer>
                  {isOpenLinkInNewTab ? (
                    <ExternalLink stroke={iconStrokeColor} data-testid="link-icon" />
                  ) : (
                    <Forward stroke={iconStrokeColor} data-testid="link-icon" />
                  )}
                </LinkIconContainer>
              </Tile>
            )}
          </ListItem>
        )}
      </UeReference>
    </>
  );
};
